// Path: frontend/src/pages/admin/components/chat-records/ChatRecordsResult.tsx

import React from 'react';
import { Loader2, MessageSquare, ChevronDown, ChevronRight, User, BookOpen, Layers, FileText } from 'lucide-react';

export interface BaseUserRecord {
  user_id: number;
  username: string;
  latest_message_time: string;
}

export interface BaseLessonRecord {
  lesson_id: number;
  lesson_title: string;
  course_id: number;
  course_title: string;
  stage_id: number;
  stage_title: string;
  lesson_sort_order: number;
}

export interface GroupedCourse {
  course_id: number;
  course_title: string;
  stages: GroupedStage[];
}

export interface GroupedStage {
  stage_id: number;
  stage_title: string;
  lessons: BaseLessonRecord[];
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

export const groupLessonsByCourseStage = <T extends BaseLessonRecord>(lessons: T[]): GroupedCourse[] => {
  const courseMap = new Map<number, {
    course_id: number;
    course_title: string;
    stages: Map<number, {
      stage_id: number;
      stage_title: string;
      lessons: T[];
    }>;
  }>();

  lessons.forEach(lesson => {
    if (!courseMap.has(lesson.course_id)) {
      courseMap.set(lesson.course_id, {
        course_id: lesson.course_id,
        course_title: lesson.course_title,
        stages: new Map()
      });
    }

    const course = courseMap.get(lesson.course_id)!;
    if (!course.stages.has(lesson.stage_id)) {
      course.stages.set(lesson.stage_id, {
        stage_id: lesson.stage_id,
        stage_title: lesson.stage_title,
        lessons: []
      });
    }

    course.stages.get(lesson.stage_id)!.lessons.push(lesson);
  });

  return Array.from(courseMap.values()).map(course => ({
    ...course,
    stages: Array.from(course.stages.values())
  }));
};

interface ChatRecordsResultProps<TUser extends BaseUserRecord> {
  records: TUser[];
  isLoading: boolean;
  hasSearched: boolean;
  totalMessages: number;
  countMessages: (user: TUser) => number;
  getLessons: (user: TUser) => BaseLessonRecord[];
  renderLessonMessages: (lesson: BaseLessonRecord, courseKey: string) => React.ReactNode;
}

function ChatRecordsResult<TUser extends BaseUserRecord>({
  records,
  isLoading,
  hasSearched,
  totalMessages,
  countMessages,
  getLessons,
  renderLessonMessages
}: ChatRecordsResultProps<TUser>) {
  const [expandedUsers, setExpandedUsers] = React.useState<Set<number>>(new Set());
  const [expandedCourses, setExpandedCourses] = React.useState<Set<string>>(new Set());
  const [expandedStages, setExpandedStages] = React.useState<Set<string>>(new Set());
  const [expandedLessons, setExpandedLessons] = React.useState<Set<string>>(new Set());

  const toggleUser = (userId: number) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleCourse = (key: string) => {
    setExpandedCourses(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleStage = (key: string) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleLesson = (key: string) => {
    setExpandedLessons(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderCourse = (course: GroupedCourse, userKey: string, lessonMessageCounts: Map<string, number>) => {
    const key = `${userKey}-course-${course.course_id}`;
    const isExpanded = expandedCourses.has(key);
    const totalMessagesInCourse = course.stages.reduce((sum, s) =>
      sum + s.lessons.reduce((lSum, l) => lSum + (lessonMessageCounts.get(`${key}-stage-${s.stage_id}-lesson-${l.lesson_id}`) || 0), 0), 0);

    return (
      <div key={course.course_id} className="border border-gray-300 rounded-lg overflow-hidden">
        <div
          className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
          onClick={() => toggleCourse(key)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <BookOpen size={16} className="text-blue-500" />
              <span className="font-semibold text-gray-800">{course.course_title}</span>
              <span className="text-sm text-gray-500">({course.stages.length} 阶段)</span>
              <span className="text-xs text-gray-400">({totalMessagesInCourse} 条消息)</span>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="p-3 space-y-2 bg-white">
            {course.stages.map(stage => renderStage(stage, key, lessonMessageCounts))}
          </div>
        )}
      </div>
    );
  };

  const renderStage = (stage: GroupedStage, courseKey: string, lessonMessageCounts: Map<string, number>) => {
    const key = `${courseKey}-stage-${stage.stage_id}`;
    const isExpanded = expandedStages.has(key);
    const totalMessagesInStage = stage.lessons.reduce((sum, l) => sum + (lessonMessageCounts.get(`${key}-lesson-${l.lesson_id}`) || 0), 0);

    return (
      <div key={stage.stage_id} className="border border-gray-300 rounded-lg overflow-hidden">
        <div
          className="bg-gradient-to-r from-gray-100 to-gray-50 p-3 cursor-pointer hover:from-gray-200 hover:to-gray-100 transition-colors"
          onClick={() => toggleStage(key)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <Layers size={16} className="text-purple-500" />
              <span className="font-medium text-gray-800">{stage.stage_title}</span>
              <span className="text-sm text-gray-500">({stage.lessons.length} 课时)</span>
              <span className="text-xs text-gray-400">({totalMessagesInStage} 条消息)</span>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="p-3 space-y-2 bg-white">
            {stage.lessons.map(lesson => renderLesson(lesson, key, lessonMessageCounts))}
          </div>
        )}
      </div>
    );
  };

  const renderLesson = (lesson: BaseLessonRecord, stageKey: string, lessonMessageCounts: Map<string, number>) => {
    const key = `${stageKey}-lesson-${lesson.lesson_id}`;
    const isExpanded = expandedLessons.has(key);
    const messageCount = lessonMessageCounts.get(key) || 0;

    return (
      <div key={lesson.lesson_id} className="border border-gray-200 rounded-lg overflow-hidden">
        <div
          className="bg-gray-50 p-3 cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-between"
          onClick={() => toggleLesson(key)}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <FileText size={14} className="text-green-500" />
            <span className="text-xs text-gray-400 font-mono">L{lesson.lesson_sort_order}</span>
            <span className="text-sm font-medium text-gray-700">{lesson.lesson_title}</span>
            <span className="text-xs text-gray-400">({messageCount} 条消息)</span>
          </div>
        </div>
        {isExpanded && (
          <div className="p-0 bg-white">
            {renderLessonMessages(lesson, key)}
          </div>
        )}
      </div>
    );
  };

  const renderUser = (user: TUser, lessonMessageCounts: Map<string, number>) => {
    const isExpanded = expandedUsers.has(user.user_id);
    const lessons = getLessons(user);
    const groupedCourses = groupLessonsByCourseStage(lessons);
    const msgCount = countMessages(user);

    return (
      <div key={user.user_id} className="border border-gray-300 rounded-lg overflow-hidden">
        <div
          className="bg-gradient-to-r from-brand-50 to-cyan-50 p-4 cursor-pointer hover:from-brand-100 hover:to-cyan-100 transition-colors"
          onClick={() => toggleUser(user.user_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                <User size={20} className="text-brand-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-800">{user.username}</div>
                <div className="text-xs text-gray-500">
                  最新消息: {user.latest_message_time ? formatDate(user.latest_message_time) : '无'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>{groupedCourses.length} 门课程</span>
              <span>{lessons.length} 个课时</span>
              <span className="font-medium text-brand-600">{msgCount} 条消息</span>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="p-4 space-y-3 bg-white">
            {groupedCourses.map(course => renderCourse(course, `user-${user.user_id}`, lessonMessageCounts))}
          </div>
        )}
      </div>
    );
  };

  const buildLessonMessageCounts = (): Map<string, number> => {
    const counts = new Map<string, number>();
    records.forEach(user => {
      const lessons = getLessons(user);
      const groupedCourses = groupLessonsByCourseStage(lessons);
      groupedCourses.forEach(course => {
        course.stages.forEach(stage => {
          stage.lessons.forEach(lesson => {
            const key = `user-${user.user_id}-course-${course.course_id}-stage-${stage.stage_id}-lesson-${lesson.lesson_id}`;
          });
        });
      });
    });
    return counts;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-brand-600 mr-2" />
        <span className="text-gray-500">加载聊天记录...</span>
      </div>
    );
  }

  if (!hasSearched) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
        <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">请设置筛选条件后点击查询按钮</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gradient-to-r from-brand-600 to-cyan-600 rounded-lg shadow-lg p-4 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare size={24} />
            <span className="text-lg font-semibold">查询结果</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <span>{records.length} 位用户</span>
            <span>{totalMessages} 条消息</span>
          </div>
        </div>
      </div>

      {records.length > 0 ? (
        <div className="space-y-4">
          {records.map(user => renderUser(user, buildLessonMessageCounts()))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">未找到符合条件的聊天记录</p>
        </div>
      )}
    </>
  );
}

export default ChatRecordsResult;
